import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  ShieldAlert, 
  ArrowRight,
  CheckCircle, 
  Eye, 
  TrendingUp, 
  BarChart3,
  Clock,
  Camera,
  AlertCircle,
  Play,
  Award,
  UserCheck,
  Bell,
  Shield,
  Activity,
  Sparkles,
  Zap,
  RefreshCw,
  Video,
  Brain,
  Settings,
  Target,
  FileText,
  Radio
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { mvsxAITestimonials } from '../../shared/testimonialData';

const MVSxAI: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  

  const features = [
    {
      icon: Camera,
      title: 'Real-time PPE & Safety Violation Detection',
      description: 'Automatically checks for helmets, vests, gloves and other critical safety gear, as well as unsafe proximity to vehicles and equipment.'
    },
    {
      icon: Brain,
      title: 'Edge AI for Low Latency',
      description: 'Performs inference directly on edge devices so alerts are generated within seconds, even in bandwidth-constrained environments.'
    },
    {
      icon: Bell,
      title: 'Risk Scoring & Prioritised Alerts',
      description: 'Each event is scored by severity and likelihood of escalation, allowing teams to focus on the most urgent situations first.'
    },
    {
      icon: BarChart3,
      title: 'Centralised Safety Dashboard',
      description: 'Gives a single view of live incidents, historical trends and compliance status across multiple plants, lines and shifts.'
    },
    {
      icon: RefreshCw,
      title: 'Mobile Notifications & Supervisor App',
      description: 'Pushes high-priority alerts to supervisors and managers on the move, complete with context frames and location details.'
    },
    {
      icon: Shield,
      title: 'Continuous Learning Pipeline',
      description: 'Models are regularly retrained with new data, improving accuracy as lighting, layouts and workflows evolve.'
    }
  ];

  const detectionCapabilities = [
    {
      icon: UserCheck,
      title: 'End-to-End Encryption: ',
      description: 'All video feeds and alert data are encrypted both in transit (TLS 1.2+) and at rest using strong encryption standards such as AES‑256.',
      features: ['Hard Hats', 'Safety Glasses', 'Face Masks', 'Safety Vests', 'Gloves', 'Safety Boots']
    },
    {
      icon: Eye,
      title: 'Edge-First Processing',
      description: 'Sensitive video is processed locally on edge devices wherever possible, sending only processed results or metadata to the cloud to limit exposure.',
      features: ['Low Latency', 'Edge Processing', 'GPU Acceleration', 'Offline Capable', 'Cloud Sync']
    },
    {
      icon: Camera,
      title: 'Role-Based Access & Governance',
      description: 'Fine-grained RBAC ensures only authorised users can access camera views, incidents and reports, aligning with internal security policies.',
      features: ['Zone Management', 'Multi-View', 'Camera Groups', 'Priority Zones', 'Coverage Maps']
    },
    {
      icon: Settings,
      title: 'Comprehensive Audit Logging',
      description: 'Every access, change and model activity is logged, creating a clear trail to support internal audits and regulatory inspections',
      features: ['Zone Rules', 'Role-Based', 'Time-Based', 'Risk Levels', 'Custom Alerts']
    },
    {
      icon: BarChart3,
      title: 'Anonymisation & Privacy Controls',
      description: 'Optional anonymisation (e.g., face blurring in stored footage) helps align with GDPR and regional data protection requirements.',
      features: ['Data Retention', 'GDPR Compliant', 'CCPA Compliant', 'Custom Policies', 'Data Purging']
    },
    {
      icon: Clock,
      title: 'Model Governance & Fair Use',
      description: 'Regular model reviews and governance processes ensure that detection performance is explainable, unbiased and aligned with company values.',
      features: ['Vulnerability Scans', 'Penetration Tests', 'Compliance Audits', 'Incident Response', 'Security Updates']
    }
  ];

  const safetyFeatures = [
    {
      icon: AlertCircle,
      title: 'Violation Logging',
      description: 'Automated violation logging with timestamps, snapshots, location, and personnel identification for complete audit trails.',
      features: ['Timestamped Logs', 'Photo Evidence', 'Location Tracking', 'Personnel ID', 'Incident Reports']
    },
    {
      icon: FileText,
      title: 'Compliance Reporting',
      description: 'Generate detailed compliance reports for audits, regulatory requirements, and safety performance reviews.',
      features: ['Audit Reports', 'Trend Analysis', 'Regulatory Compliance', 'KPI Dashboards', 'Export Options']
    },
    {
      icon: Radio,
      title: 'Alert Escalation',
      description: 'Multi-level alert routing with escalation rules ensuring critical violations reach the right personnel immediately.',
      features: ['SMS Alerts', 'Email Notifications', 'Mobile Push', 'Escalation Rules', 'On-Call Routing']
    },
    {
      icon: Activity,
      title: 'Real-Time Monitoring',
      description: 'Live monitoring dashboards showing current compliance status, active alerts, and zone-wise safety metrics.',
      features: ['Live Dashboard', 'Zone Status', 'Active Alerts', 'Heat Maps', 'Shift Reports']
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Fewer Incidents & Near-Misses',
      description: 'Real-time monitoring and faster response reduce the likelihood and severity of accidents on site.',
      metric: '60% fewer incidents'
    },
    {
      icon: Award,
      title: 'Reduced Downtime & Disruption',
      description: 'Preventing incidents means fewer shutdowns, investigations and rework, keeping production schedules on track.',
      metric: '95%+ compliance'
    },
    {
      icon: Clock,
      title: 'Lower Regulatory & Insurance Exposure',
      description: 'Consistent compliance and data-backed safety practices help reduce fines, claims and insurance premiums over time.',
      metric: '70% time savings'
    },
    {
      icon: Shield,
      title: 'Better Use of Supervisor Time',
      description: 'Supervisors can focus on coaching and process improvement instead of constantly patrolling for basic PPE violations.',
      metric: '80% risk reduction'
    },
    {
      icon: UserCheck,
      title: 'Stronger Safety Culture',
      description: 'Transparent, data-driven insights encourage constructive conversations about safety and reinforce the message that incidents are preventable.',
      metric: '50% morale boost'
    },
  ];

  const integrations = [
    {
      category: 'Camera Systems',
      tools: ['IP Cameras', 'CCTV Systems', 'PTZ Cameras', 'Thermal Cameras', 'Dome Cameras', 'Edge Cameras']
    },
    {
      category: 'Safety Systems',
      tools: ['Access Control', 'Badge Systems', 'Emergency Alerts', 'Incident Management', 'Safety Apps', 'EHS Software']
    },
    {
      category: 'Analytics Platforms',
      tools: ['Power BI', 'Tableau', 'Grafana', 'Custom APIs', 'SIEM Systems', 'BI Tools']
    },
    {
      category: 'Communication',
      tools: ['Slack', 'Microsoft Teams', 'SMS Gateway', 'Email', 'WhatsApp', 'Radio Systems']
    }
  ];

  const capabilities = [
    {
      title: 'All-Weather Performance',
      description: 'Operates reliably in varying lighting conditions including low light, bright sunlight, rain, and dust with adaptive algorithms.'
    },
    {
      title: 'Privacy Protection',
      description: 'Privacy-first design with optional face blurring, anonymized tracking, and GDPR-compliant data handling.'
    },
    {
      title: 'Scalable Architecture',
      description: 'Deploy from single sites to enterprise-wide installations with centralized management and distributed processing.'
    },
    {
      title: 'Existing Infrastructure',
      description: 'Works with existing security cameras and CCTV systems—no need for specialized hardware or complete replacement.'
    }
  ];

  const solutions = [
    {
      icon: Target, 
      title: 'Manufacturing Plants',
      description: 'Monitors assembly lines, welding bays, paint shops and material-handling zones for PPE usage and unsafe movement. Helps reduce lost-time injuries and keeps production lines running without unplanned shutdowns.'
    },
    {
      icon: Zap,
      title: 'Construction & Infrastructure Projects',
      description: 'Keeps an eye on constantly changing sites, entrances and work-at-height zones where manual supervision is hard. Ensures contractors and subcontractors follow site rules, improving overall project safety and compliance.'
    },
    { icon: Sparkles,
      title: 'Oil, Gas & Chemical Facilities',
      description: 'Watches critical areas such as loading racks, tank farms and process units for violations and high-risk scenarios. Supports strict regulatory standards where even minor incidents can carry major financial and environmental impact.'
    },
    {
      icon: RefreshCw,
      title: 'Warehousing & Logistics Hubs',
      description: 'Monitors forklift traffic, loading bays and pedestrian walkways to reduce collisions and near-misses. Helps logistics operators protect staff while keeping throughput high and predictable.'
    },
    {
      icon: Clock,
      title: 'Multi-Site Industrial Enterprises',
      description: 'Provides a uniform safety lens across geographically dispersed plants and yards. Leadership can benchmark performance, share best practices and roll out improvements based on consistent data.'
    },

  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-green-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 neon-glow">
                  <ShieldAlert className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-green-400 uppercase tracking-wide">Computer Vision Safety Guard</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent">MVS x AI</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">24/7 PPE Compliance Monitoring</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
               MVS x AI turns your existing CCTV network into an intelligent safety layer for the entire shop floor. Using advanced computer vision and edge AI, it watches live video streams around the clock, spotting missing PPE, unsafe behaviour and high-risk situations the moment they appear. Instead of relying solely on periodic inspections and human vigilance, supervisors get real-time alerts, clear evidence snapshots and actionable insights across all their plants. The result is fewer incidents, smoother operations and peace of mind that safety standards are being followed even when no one is physically watching the cameras.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-green-500/25 transition-all duration-300"
                >
                  Deploy Safety AI
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-500">24/7</div>
                  <div className="text-sm text-muted-foreground">Monitoring</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-400">95%+</div>
                  <div className="text-sm text-muted-foreground">Accuracy</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">Real-Time</div>
                  <div className="text-sm text-muted-foreground">Alerts</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-gradient-to-r from-green-500 to-emerald-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Products/ai-Products/mvsxAi.png'
                    alt="MVS x AI Safety Monitoring System"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-green-500/20 rounded-lg backdrop-blur-sm">
                          <ShieldAlert className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="font-semibold">AI-Powered Safety</p>
                          <p className="text-sm text-white/80">PPE Compliance Guard</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-green-500">
                    <Activity className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">LIVE</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
             Core <span className="brand-gradient-text">Features</span>
            </h2>
            {/* <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              MVS x AI combines advanced computer vision, machine learning, and real-time analytics to create an intelligent safety monitoring system that operates continuously, detects violations instantly, and prevents incidents before they occur.
            </p> */}
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-strong p-8 rounded-2xl hover:scale-105 transition-all duration-300"
                >
                  <div className="mb-6 p-4 rounded-xl bg-green-500/20 w-fit">
                    <Icon className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-justify">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detection Flow Section with Embedded Image */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              <span className="brand-gradient-text">Solutions</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
             MVS x AI is built for industrial organisations where a single safety lapse can stop production, damage assets or put lives at risk. It adapts to different layouts, processes and risk profiles while using the same core platform to deliver consistent safety outcomes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={solution.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-strong p-8 rounded-2xl hover:scale-105 transition-all duration-300"
                >
                  <div className="mb-6 p-4 rounded-xl bg-green-500/20 w-fit">
                    <Icon className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{solution.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-justify">
                    {solution.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Flow Section with Embedded Image */}
      <section className="relative py-20 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              End-to-End Safety Workflow
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              From video capture to actionable insights, MVS x AI provides a complete safety monitoring solution with seamless integration and instant response.
            </p>
          </motion.div>

          {/* Embedded Process Flow Diagram */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16"
          >
          </motion.div>

          {/* Process Steps Description */}
          <div className="relative">
            {/* Desktop Flow */}
            <div className="hidden lg:flex items-center justify-between gap-4">
              {[
                { icon: Video, title: 'Video Capture', desc: '24/7 camera feeds monitoring', color: 'green-500' },
                { icon: Brain, title: 'AI Analysis', desc: 'Computer vision PPE detection', color: 'emerald-500' },
                { icon: AlertCircle, title: 'Violation Detection', desc: 'Identify safety equipment issues', color: 'green-600' },
                { icon: Bell, title: 'Real-time Alerts', desc: 'Instant supervisor notifications', color: 'emerald-600' },
                { icon: BarChart3, title: 'Reporting', desc: 'Compliance analytics & insights', color: 'green-500' }
              ].map((step, index) => (
                <React.Fragment key={step.title}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.15 }}
                    viewport={{ once: true }}
                    className="flex-1 text-center"
                  >
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="relative mb-6 mx-auto"
                    >
                      <div className={`w-24 h-24 rounded-full bg-gradient-to-br from-${step.color}/20 to-${step.color}/10 flex items-center justify-center glass-strong border-2 border-${step.color}/30 mx-auto`}>
                        <step.icon className={`w-10 h-10 text-${step.color}`} />
                      </div>
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className={`absolute inset-0 rounded-full bg-${step.color}/10 blur-xl`}
                      />
                    </motion.div>
                    <h4 className="font-semibold text-lg mb-2">{step.title}</h4>
                    <p className="text-sm text-muted-foreground text-center">{step.desc}</p>
                  </motion.div>
                  
                  {index < 4 && (
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.15 + 0.3 }}
                      viewport={{ once: true }}
                      className="flex-shrink-0 self-start mt-12"
                    >
                      <div className="relative">
                        <ArrowRight className="w-8 h-8 text-primary" />
                        <motion.div
                          animate={{ x: [0, 10, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          className="absolute inset-0"
                        >
                          <ArrowRight className="w-8 h-8 text-primary/30" />
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Mobile Flow */}
            <div className="lg:hidden space-y-6">
              {[
                { icon: Video, title: 'Video Capture', desc: '24/7 camera feeds monitoring', color: 'green-500' },
                { icon: Brain, title: 'AI Analysis', desc: 'Computer vision PPE detection', color: 'emerald-500' },
                { icon: AlertCircle, title: 'Violation Detection', desc: 'Identify safety equipment issues', color: 'green-600' },
                { icon: Bell, title: 'Real-time Alerts', desc: 'Instant supervisor notifications', color: 'emerald-600' },
                { icon: BarChart3, title: 'Reporting', desc: 'Compliance analytics & insights', color: 'green-500' }
              ].map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-strong p-6 rounded-xl"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-${step.color}/20 to-${step.color}/10 flex items-center justify-center flex-shrink-0 border-2 border-${step.color}/30`}>
                      <step.icon className={`w-8 h-8 text-${step.color}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">{step.title}</h4>
                      <p className="text-sm text-muted-foreground">{step.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Detection Capabilities Section */}
      <section className="relative py-20 bg-gradient-to-b from-transparent via-secondary/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Data Security &<span className="brand-gradient-text"> Compliance</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Safety data is sensitive, and the platform is designed with that in mind from day one. MVS x AI follows enterprise-grade security practices so operations, IT and compliance teams can all be comfortable with how video and alerts are handled.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {detectionCapabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={capability.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-green-500/20">
                      <Icon className="w-8 h-8 text-green-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{capability.title}</h3>
                      <p className="text-muted-foreground text-justify">{capability.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 hidden">
                    {capability.features.map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Safety Features Section */}
      <section className="relative py-20 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Comprehensive Safety Management
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Complete safety monitoring solution with automated logging, compliance reporting, and intelligent alert systems for industrial environments.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {safetyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-emerald-500/20">
                      <Icon className="w-8 h-8 text-emerald-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground text-justify">{feature.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {feature.features.map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-20 bg-gradient-to-b from-transparent via-accent/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Platform <span className="brand-gradient-text"> Benefits</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              The value of MVS x AI goes well beyond catching someone without a helmet. It directly supports business outcomes that matter to industrial leaders: safer people, more reliable production and stronger financial performance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-strong p-6 rounded-xl text-center hover:scale-105 transition-all duration-300"
                >
                  <div className="mb-4 p-4 rounded-xl bg-green-500/20 w-fit mx-auto">
                    <Icon className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 text-justify">
                    {benefit.description}
                  </p>
                  <div className="text-2xl font-bold text-green-500 hidden">
                    {benefit.metric}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="relative py-20 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Seamless Integrations
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Connect MVS x AI with your existing camera systems, safety platforms, and communication tools for unified safety operations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {integrations.map((integration, index) => (
              <motion.div
                key={integration.category}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-strong p-6 rounded-xl"
              >
                <h3 className="text-lg font-semibold mb-4 text-primary">{integration.category}</h3>
                <div className="space-y-2">
                  {integration.tools.map((tool) => (
                    <div key={tool} className="flex items-center gap-2">
                      <Zap className="w-3 h-3 text-accent flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{tool}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Capabilities */}
      <section className="relative py-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Enterprise-Grade Capabilities
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Built for industrial environments with robust performance, privacy protection, and scalable architecture.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    <Sparkles className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3">{capability.title}</h3>
                    <p className="text-muted-foreground leading-relaxed text-justify">
                      {capability.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-20 hidden">
        <ProductTestimonials 
          testimonials={mvsxAITestimonials}
          title="Safety Success Stories"
          subtitle="See how organizations enhanced workplace safety with MVS x AI"
          gradientFrom="#10B981"
          gradientVia="#059669"
          gradientTo="#10B981"
          iconColor="#10B981"
          dotColor="bg-green-500"
          speed={60}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Ready to Transform Workplace Safety?
            </h2>
            <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto text-center">
              Deploy MVS x AI and experience the power of intelligent safety monitoring with real-time alerts, continuous learning, and comprehensive compliance reporting.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-green-500/25 transition-all duration-300"
              >
                Get Started Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="glass inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Schedule Demo
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { MVSxAI };
export default MVSxAI;